import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, {
  useEffect, useState, useMemo, useCallback,
} from 'react';
import useSound from 'use-sound';
import { usePaginatedQuery } from '@playbookux/playbook-web-core';

import {
  Loader,
} from 'semantic-ui-react';

import { GET_USER_BY_ID } from '../../../core/graphql/user';
import ping from './notification.mp3';
import { GET_PRACTICE_TEST_BY_USER } from '../../../core/graphql/test-sessions';
import { MATCHED_SEGMENTS_QUERY, ONGOING_STUDIES_QUERY } from '../../../core/graphql/screener-sessions';
import {
  PRACTICE_TEST_STATUS, USER_STATUS,
} from './constants';
import Sidebar from './sidebar';
import Studies from './studies';
import Tabs from '../../constants/tabs';
import withLayout from '../../../core/components/with-layout';

import {
  Bold,
  Centered,
  Container,
  Header,
  Instructions,
  Segment,
  Text,
  Empty,
  Loading,
} from './styles';
import { isCompletedProfile, mapProjectSegment } from './helpers';
import withGa from '../../../core/components/with-ga';
import { registerFunctions } from '../../../core/hooks/use-function-handler';

const BrowseTests = (props) => {
  const {
    history,
    profile: profileState,
    alert,
  } = props;

  const {
    user_id: userId,
  } = profileState || {};

  const [title, setTitle] = useState('PlaybookUX');
  /** State */
  const [filters, setFilters] = useState([
    'unmoderated',
    'moderated',
    'card_sort',
    'tree_test',
    'survey',
  ]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [play] = useSound(ping);

  /** Queries */
  const {
    data: { user = {} } = {},
    loading: userLoading,
  } = useQuery(GET_USER_BY_ID, {
    variables: { userId },
  });

  const matchedProjectsAttributes = {
    variables: {
      userId,
      filters,
      order: 'created_at DESC',
    },
    fetchPolicy: 'network-only',
  };
  const unfinishedProjectsAttributes = {
    variables: {
      userId,
      filters,
    },
  };

  const {
    data: { userPracticeTest = {} } = {},
    loading: practiceTestLoading,
  } = useQuery(GET_PRACTICE_TEST_BY_USER, {
    variables: { userId },
  });

  const {
    data: findMatchedProjectsSegmentsData,
    fetchMore,
    hasMore,
    refetch,
    loading: loadingMatchedProjects,
  } = usePaginatedQuery(
    MATCHED_SEGMENTS_QUERY,
    matchedProjectsAttributes,
    { dataField: 'findMatchedProjectsSegments' },
  );

  const findMatchedProjectsSegments = findMatchedProjectsSegmentsData?.findMatchedProjectsSegments || [];

  const {
    data: ongoingStudiesData,
    refetch: refetchOngoing,
    loading: loadingOnGoing,
  } = usePaginatedQuery(
    ONGOING_STUDIES_QUERY,
    unfinishedProjectsAttributes,
    { dataField: 'ongoingStudies' },
  );
  const { ongoingStudies = [] } = ongoingStudiesData || {};

  const loading = loadingMatchedProjects || loadingOnGoing;

  const profileIsComplete = !_.isEmpty(user) && isCompletedProfile(user.profile);

  const refetchAllStudies = useCallback(() => {
    refetch();
    refetchOngoing();
  }, [filters, refetch, refetchOngoing]);

  useEffect(refetchAllStudies, [refetchAllStudies]);

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  const onSelection = (selectedFilters) => {
    setFilters(selectedFilters);
  };

  const hasPracticeTest = userPracticeTest && !_.isEmpty(userPracticeTest);
  const practiceTestApproved = hasPracticeTest && userPracticeTest.practiceTestStatus === PRACTICE_TEST_STATUS.APPROVED;
  const practiceTestNotSubmitted = hasPracticeTest && userPracticeTest.practiceTestStatus === PRACTICE_TEST_STATUS.NOT_SUBMITTED;
  const practiceTestSubmitted = hasPracticeTest && userPracticeTest.practiceTestStatus === PRACTICE_TEST_STATUS.SUBMITTED;
  const practiceTestRejected = hasPracticeTest && userPracticeTest.practiceTestStatus === PRACTICE_TEST_STATUS.REJECTED;
  const practiceTestBlocked = hasPracticeTest && userPracticeTest.practiceTestStatus === PRACTICE_TEST_STATUS.BLOCKED;
  const numberOfProjects = findMatchedProjectsSegments?.length || 0;

  const isUserPendingWithoutPracticeTest = !hasPracticeTest && user.status === USER_STATUS.PENDING;
  const isUserNotApprovedWithUnSubmittedTest = practiceTestNotSubmitted && user.status !== USER_STATUS.APPROVED;

  useEffect(() => {
    if (user.status !== USER_STATUS.APPROVED) {
      setTitle('PlaybookUX');
      return;
    }

    setTitle(`PlaybookUX (${numberOfProjects} tests)`);
    document.title = title;
    if (numberOfProjects > 0 && alert.notification) {
      setInterval(play, 300000);
    }
  }, [alert.notification, numberOfProjects, play, title, user.status]);

  const noMatchedProjects = useMemo(() => !(findMatchedProjectsSegments.length || ongoingStudies.length), [
    findMatchedProjectsSegments.length, ongoingStudies.length]);

  const mappedOngoingStudies = useMemo(() => {
    if (ongoingStudies.length > 0) {
      return ongoingStudies.map((projectSegment) => mapProjectSegment(projectSegment));
    }
    return [];
  }, [ongoingStudies]);

  const mappedMatchedProjectsSegments = useMemo(() => {
    if (findMatchedProjectsSegments.length > 0) {
      return findMatchedProjectsSegments.map((projectSegment) => mapProjectSegment(projectSegment));
    }
    return [];
  }, [findMatchedProjectsSegments]);

  registerFunctions('browse_tests', {
    func1: refetchAllStudies,
  });

  if (practiceTestLoading || userLoading) {
    return (
      <Container>
        <Centered>
          <Loader active data-loader="browse-tests-loader" />
        </Centered>
      </Container>
    );
  }

  if ((user.status === USER_STATUS.APPROVED && !practiceTestApproved && !profileIsComplete)) {
    return (
      <Container>
        <Centered>
          <Header as="h3">You can&apos;t browse/take tests until your profile is complete.</Header>
          <Header as="h3">
            Head to the
            {' '}
            <Link to="/profile">My Profile</Link>
            {' '}
            tab to complete your profile.
          </Header>
        </Centered>
      </Container>
    );
  }

  if (user.status === USER_STATUS.APPROVED && practiceTestApproved && !profileIsComplete) {
    history.push('/profile');
    return null;
  }

  if (isUserPendingWithoutPracticeTest || isUserNotApprovedWithUnSubmittedTest) {
    history.push('/video-instruction');
    return null;
  }

  if (practiceTestBlocked) {
    history.push('/blocked');
    return null;
  }

  if (practiceTestRejected) {
    history.push('/rejected');
    return null;
  }

  /* The rejected user is in pending approval until this new screen is not created */
  if ((user.status === USER_STATUS.PENDING && practiceTestSubmitted)) {
    return (
      <Container>
        <Segment>
          <Header as="h3">Your practice test is submitted.</Header>
          <Instructions>
            <Text>
              The PlaybookUX team will review your video -
              <Bold> there’s no further action required</Bold>
              . We’ll let you know if you’re approved to start testing within 7 days.
            </Text>
          </Instructions>
        </Segment>
      </Container>
    );
  }

  return (
    <Container>
      {!isMobile && (
        <Sidebar onSelection={onSelection} />
      )}
      {loading && (
        <Loading />
      )}
      {!loading && noMatchedProjects && (
        <Empty />
      )}
      {!loading && !noMatchedProjects && (
        <Studies
          testerStudies={mappedMatchedProjectsSegments}
          ongoingStudies={mappedOngoingStudies}
          hasMore={hasMore}
          userId={userId}
          onLoadMore={fetchMore}
        />
      )}
    </Container>
  );
};

BrowseTests.propTypes = {
  profile: PropTypes.shape({
    user_id: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  alert: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({}),
  ]).isRequired,
};

const mapStateToProps = ({ authentication, notification }) => ({
  profile: authentication.profile,
  alert: notification,
});

export default compose(
  withLayout({ selectedTab: Tabs.BROWSE_TESTS }),
  connect(mapStateToProps, null),
  withGa,
)(BrowseTests);
